import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { AccountDetail, ClaimCode } from '../../../api/accounts'
import { api } from '../../../api/client'
import { formatDateTime, formatRelative } from '../../../lib/format'
import { ClaimCodeNotice } from './ClaimCodeNotice'
import { EnvironmentBadge, StatusBadge } from './StatusBadge'
import { useAccountAction } from './useAccountAction'

/** Effective access and the account-wide actions: refresh from Apple, suspend or lift, claim code, delete. */
export function AccessCard({ detail }: { detail: AccountDetail }) {
	const { account, suspensions } = detail
	const navigate = useNavigate()
	const [reason, setReason] = useState('')
	const [claim, setClaim] = useState<ClaimCode | null>(null)
	const refresh = useAccountAction(account.id, () =>
		api.post<{ subscriptions: number }>(`/api/v1/admin/accounts/${account.id}/refresh`),
	)
	const suspend = useAccountAction(account.id, () =>
		api.post(`/api/v1/admin/accounts/${account.id}/suspensions`, { reason }).then(() => setReason('')),
	)
	const lift = useAccountAction(account.id, () => api.delete(`/api/v1/admin/accounts/${account.id}/suspensions`))
	const code = useAccountAction(account.id, () =>
		api.post<ClaimCode>(`/api/v1/admin/accounts/${account.id}/claim-codes`).then(setClaim),
	)
	const remove = useAccountAction(account.id, () =>
		api.delete(`/api/v1/admin/accounts/${account.id}`).then(() => navigate('/admin?tab=accounts')),
	)
	const open = suspensions.find((s) => !s.liftedAt)
	const failure = [refresh, suspend, lift, code, remove].find((m) => m.isError)?.error
	const message =
		failure?.message === 'appstore_api_unconfigured'
			? 'Refreshing needs the App Store Server API key (APPSTORE_API_* in the server’s .env).'
			: failure?.message

	return (
		<section className="card stack">
			<div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
				<div>
					<StatusBadge status={account.status} />
					<EnvironmentBadge environment={account.environment} />
					<div className="small muted" style={{ marginTop: '0.4rem' }}>
						{account.plan ? `Plan ${account.plan}` : 'No plan'}
						{account.source && ` · from a ${account.source}`}
						{account.expiresAt && ` · until ${formatDateTime(account.expiresAt)}`}
						{account.willRenew !== null && ` · ${account.willRenew ? 'renews' : 'won’t renew'}`}
					</div>
					<div className="small muted">
						Created {formatDateTime(account.createdAt)} · last seen {formatRelative(account.lastSeenAt)}
					</div>
				</div>
				<div className="row" style={{ flexWrap: 'wrap' }}>
					<button type="button" onClick={() => refresh.mutate(undefined)} disabled={refresh.isPending}>
						Refresh from Apple
					</button>
					<button type="button" onClick={() => code.mutate(undefined)}>
						New claim code
					</button>
					<button
						type="button"
						className="error"
						onClick={() =>
							window.confirm(`Delete ${account.supportId} and everything in it? This can’t be undone.`) &&
							remove.mutate(undefined)
						}
					>
						Delete
					</button>
				</div>
			</div>
			{refresh.data && <p className="small muted">Updated {refresh.data.subscriptions} subscription(s) from Apple.</p>}
			{message && <p className="error small">{message}</p>}
			{claim && <ClaimCodeNotice claim={claim} />}
			{open ? (
				<div className="row">
					<p className="small" style={{ margin: 0, flex: 1 }}>
						Suspended since {formatDateTime(open.startsAt)}: {open.reason}
						{open.endsAt && ` (until ${formatDateTime(open.endsAt)})`}
					</p>
					<button type="button" onClick={() => lift.mutate(undefined)}>
						Lift suspension
					</button>
				</div>
			) : (
				<form className="row" onSubmit={(e) => (e.preventDefault(), reason.trim() && suspend.mutate(undefined))}>
					<input
						aria-label="Suspension reason"
						placeholder="Reason to suspend"
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						style={{ flex: 1 }}
					/>
					<button type="submit" disabled={!reason.trim()}>
						Suspend
					</button>
				</form>
			)}
		</section>
	)
}
